// view.js — Router (imports all tool modules)
import { initSearchTable } from "./tools/search-table/view";
import { initSearchInPage } from "./tools/search-in-page/view";
import { initEventLogo } from "./tools/event-logo/view";
import { initPrimaryLogo } from "./tools/primary-logo/view";
import { initTableOfContents } from "./tools/table-of-contents/view";
import { initCustomMenu } from "./tools/custom-menu/view";
import { initCalendarButton } from "./tools/calendar-button/view";
import { initExtractElement, replaceCalendarIcons } from "./tools/extract-element/view";
import { initAccordionSearch } from "./tools/accordion-search/view";
import { initBannerImage } from "./tools/banner-image/view";

/* ===========================================================
   DOMContentLoaded — init all tools
   =========================================================== */
document.addEventListener("DOMContentLoaded", function () {
	initSearchTable();
	initPrimaryLogo();
	initEventLogo();
	initTableOfContents();
	initCustomMenu();
	initExtractElement();
	replaceCalendarIcons();
	initBannerImage();
	initSearchInPage();
	initModalSupport();
});

/* ===========================================================
   Accordion Search (needs jQuery, self-inits)
   =========================================================== */
(function () {
	window.initAccordionSearch = initAccordionSearch;
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initAccordionSearch);
	} else {
		initAccordionSearch();
	}
})();

/* ===========================================================
   Calendar Button (MutationObserver-based, self-managing)
   =========================================================== */
initCalendarButton();

/* ===========================================================
   Webcast Patch (LIVE + ARCHIVE)
   =========================================================== */
document.addEventListener("webcastLoaded", (ev) => {
	const extract = ev.detail.container;
	const url = ev.detail.url || "";
	if (!extract || !url.includes("webcast")) return;

	function b64ToUrl(b64) {
		if (!b64) return "";
		try {
			b64 = b64.replace(/-/g, "+").replace(/_/g, "/");
			while (b64.length % 4) b64 += "=";
			const decoded = atob(b64);
			return /^https?:\/\//.test(decoded) ? decoded : "https:" + decoded;
		} catch {
			return "";
		}
	}

	function resolvePlaybackTarget(a) {
		const isSmall = window.matchMedia("(max-width:768px)").matches;
		const web = b64ToUrl(a.getAttribute("data-web-url") || "");
		const mob = b64ToUrl(a.getAttribute("data-mobile-url") || "");
		const best = isSmall ? mob || web : web || mob;
		if (!best) return null;
		if (/\.(mp4|m4v|webm|ogv|m3u8)/i.test(best)) return { type: "video", url: best };
		return { type: "iframe", url: best };
	}

	function wrapLangButtons(root) {
		root.querySelectorAll("td").forEach((td) => {
			if (td.querySelector("ul.webcast-lang-list")) return;
			const links = [...td.querySelectorAll("a.video-link")];
			if (!links.length) return;
			const ul = document.createElement("ul");
			ul.className = "webcast-lang-list";
			td.insertBefore(ul, td.firstChild);
			links.forEach((a) => {
				const li = document.createElement("li");
				li.appendChild(a);
				ul.appendChild(li);
			});
			td.querySelectorAll("br").forEach((br) => br.remove());
		});
	}

	function stopAllPlayers() {
		const h = extract.querySelector(".view-video-player");
		if (!h) return;
		h.querySelectorAll("video").forEach((v) => v.pause());
		h.querySelectorAll("iframe").forEach((i) => (i.src = "about:blank"));
	}

	function mountVideoPlayer(videoUrl) {
		const h = extract.querySelector(".view-video-player");
		if (!h) return;
		stopAllPlayers();
		h.innerHTML = "";
		const v = document.createElement("video");
		Object.assign(v, { controls: true, autoplay: true, playsInline: true, preload: "auto" });
		v.style = "width:100%;max-height:70vh;background:black;";
		v.src = videoUrl;
		h.appendChild(v);
		v.play().catch(() => {});
	}

	function mountIframePlayer(iframeUrl) {
		const h = extract.querySelector(".view-video-player");
		if (!h) return;
		stopAllPlayers();
		h.innerHTML = "";
		const f = document.createElement("iframe");
		f.src = iframeUrl;
		f.allow = "autoplay; encrypted-media";
		f.allowFullscreen = true;
		f.style = "width:100%;height:65vh;border:0;background:black;";
		h.appendChild(f);
	}

	function playFromLink(a) {
		if (a.getAttribute("data-video-type") === "live") return mountIframePlayer(a.href);
		const target = resolvePlaybackTarget(a);
		if (!target) return;
		target.type === "video" ? mountVideoPlayer(target.url) : mountIframePlayer(target.url);
	}

	extract.addEventListener("click", (e) => {
		const a = e.target.closest("a.video-link");
		if (!a) return;
		e.preventDefault();
		extract.querySelectorAll("a.video-link.is-active").forEach((x) => x.classList.remove("is-active"));
		a.classList.add("is-active");
		playFromLink(a);
	});

	const holder = extract.querySelector(".view-video-player");
	if (holder) holder.innerHTML = "";

	function fixWebcastTitles(root) {
		root.querySelectorAll("td.views-field-field-computed-title").forEach((td) => {
			td.querySelectorAll(".webcast-title-block").forEach((n) => n.remove());
			const strongs = td.querySelectorAll("strong");
			strongs.forEach((s, i) => i > 0 && s.remove());
			const strong = td.querySelector("strong");
			const langList = td.querySelector("ul.webcast-lang-list");
			if (!strong || !langList) return;
			const strongText = strong.textContent.trim();
			td.querySelectorAll("div").forEach((div) => {
				if (div.textContent.trim() === strongText) div.remove();
			});
			const room = td.textContent.match(/\(.*?\)/)?.[0] || "";
			const wrap = document.createElement("div");
			wrap.className = "webcast-title-block";
			const t = document.createElement("div");
			t.className = "webcast-title";
			t.textContent = strongText;
			wrap.appendChild(t);
			if (room) {
				const r = document.createElement("div");
				r.className = "webcast-room";
				r.textContent = room;
				wrap.appendChild(r);
			}
			td.insertBefore(wrap, langList);
		});
	}

	const observer = new MutationObserver(() => {
		wrapLangButtons(extract);
		fixWebcastTitles(extract);
	});
	wrapLangButtons(extract);
	fixWebcastTitles(extract);
	observer.observe(extract, { childList: true, subtree: true });
});

/* ===========================================================
   Force accordion search at window.load
   =========================================================== */
window.addEventListener("load", () => {
	if (typeof window.initAccordionSearch === "function") {
		window.initAccordionSearch();
	}
});

/* ===========================================================
   Modal support (Bootstrap 4/5 + fallback)
   =========================================================== */
function initModalSupport() {
	// Upgrade B4 → B5
	document.querySelectorAll('a[data-toggle="modal"]').forEach((a) => {
		if (!a.hasAttribute("data-bs-toggle")) a.setAttribute("data-bs-toggle", "modal");
		const tgt = a.getAttribute("data-target");
		if (tgt && !a.hasAttribute("data-bs-target")) a.setAttribute("data-bs-target", tgt);
	});

	// Click handler
	document.querySelectorAll('a[data-bs-toggle="modal"], a[data-toggle="modal"]').forEach((a) => {
		a.addEventListener("click", (e) => {
			e.preventDefault();
			const target = a.getAttribute("data-bs-target") || a.getAttribute("data-target");
			if (!target) return;
			const idSel = target.startsWith("#") ? target : `#${target}`;
			const modalEl = document.querySelector(idSel);
			if (!modalEl) return;
			if (window.bootstrap && window.bootstrap.Modal) {
				window.bootstrap.Modal.getOrCreateInstance(modalEl).show();
				return;
			}
			if (window.jQuery && typeof window.jQuery(modalEl).modal === "function") {
				window.jQuery(modalEl).modal("show");
			}
		});
	});

	// Ensure loginModal
	if (!document.getElementById("loginModal")) {
		document.body.insertAdjacentHTML("beforeend",
			`<div class="modal fade" id="loginModal" tabindex="-1" role="dialog" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered" role="document">
					<div class="modal-content" style="border-radius:10px;overflow:hidden;">
						<div class="modal-header" style="border-bottom:none;">
							<h5 class="modal-title">ITU Login Required</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body" style="padding:20px;">
							<p>You need to log in to access this content.<br>
								<a href="https://www.itu.int/en/myitu" target="_blank" rel="noopener" class="btn btn-primary mt-3">Go to MyITU Login</a>
							</p>
						</div>
					</div>
				</div>
			</div>`);
	}

	// Fallback sans Bootstrap
	if (!window.bootstrap && !(window.jQuery && window.jQuery.fn.modal)) {
		if (!document.getElementById("itu-modal-fallback-styles")) {
			const s = document.createElement("style");
			s.id = "itu-modal-fallback-styles";
			s.textContent = `
#ituLoginOverlay{position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,.5);display:flex;justify-content:center;align-items:center;z-index:999998;opacity:0;visibility:hidden;pointer-events:none;transition:opacity .3s ease}
#ituLoginOverlay.active{opacity:1;visibility:visible;pointer-events:auto}
#ituLoginOverlay .itu-fallback-modal{position:fixed;top:50%;left:50%;width:90%;max-width:600px;min-width:320px;max-height:85vh;background:#fff;border-radius:8px;box-shadow:0 0 1.5rem rgba(169,202,216,.078);font-family:system-ui,sans-serif;transform:translate(-50%,-48%) scale(.95);opacity:0;visibility:hidden;pointer-events:none;transition:transform .25s cubic-bezier(.25,.8,.25,1),opacity .25s ease-out;will-change:transform,opacity;overflow:hidden;z-index:999999}
#ituLoginOverlay.active .itu-fallback-modal{transform:translate(-50%,-50%) scale(1);opacity:1;visibility:visible;pointer-events:auto}
#ituLoginOverlay .itu-fallback-header{background-color:rgb(229,245,251);padding:12px 16px;border-bottom:2px solid #009dd9;display:flex;align-items:baseline;justify-content:space-between;width:100%;border-top-left-radius:8px;border-top-right-radius:8px}
#ituLoginOverlay .itu-fallback-header h2{font-size:1.1em;color:#003764;margin:0}
#ituLoginOverlay .itu-fallback-close{font-size:24px;cursor:pointer;font-weight:700;color:#666}
#ituLoginOverlay .itu-fallback-body{padding:20px;text-align:center}
#ituLoginOverlay .itu-fallback-body a{display:inline-block;background:#009cd6;color:#fff;padding:8px 18px;border-radius:4px;text-decoration:none;font-weight:500;margin-top:10px}
#ituLoginOverlay .itu-fallback-body a:hover{background:#007fb2}
#ituLoginOverlay .itu-fallback-body button{background:none;border:none;color:#777;cursor:pointer;margin-top:16px}`;
			document.head.appendChild(s);
		}

		function ensureModal() {
			if (document.getElementById("ituLoginOverlay")) return;
			document.body.insertAdjacentHTML("beforeend",
				`<div id="ituLoginOverlay" aria-hidden="true">
					<div class="itu-fallback-modal" role="dialog" aria-modal="true" id="ituLoginModal">
						<div class="itu-fallback-header">
							<h2>ITU Login Required</h2>
							<span class="itu-fallback-close" id="ituLoginClose">&times;</span>
						</div>
						<div class="itu-fallback-body">
							<p>You need to log in to access this content.</p>
							<a href="https://www.itu.int/en/myitu" target="_blank" rel="noopener">Go to MyITU Login</a><br>
							<button id="ituLoginCloseBtn">Close</button>
						</div>
					</div>
				</div>`);
		}

		document.addEventListener("click", (e) => {
			const a = e.target.closest('a[data-bs-target="#loginModal"], a[data-target="#loginModal"]');
			if (a) {
				e.preventDefault();
				ensureModal();
				const overlay = document.getElementById("ituLoginOverlay");
				if (overlay) overlay.classList.add("active");
			}
		});

		document.addEventListener("click", (e) => {
			if (e.target.matches("#ituLoginClose, #ituLoginCloseBtn, #ituLoginOverlay")) {
				const overlay = document.getElementById("ituLoginOverlay");
				if (overlay) overlay.classList.remove("active");
			}
		});
	}
}